import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges
} from '@angular/core';
import { GoogleChartsLoaderService } from './google-charts-loader.service';
import { ChartErrorEvent, ChartReadyEvent, ChartType, getPackageForChartType } from './google-charts.types';

@Component({
  selector: 'google-chart',
  template: '',
  styles: [':host { display: block; width: fit-content; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GoogleChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() type: ChartType;
  @Input() data: Array<Array<string | number>>;
  @Input() columns: Array<unknown>;
  @Input() title: string;
  @Input() width: number;
  @Input() height: number;
  @Input() options: Record<string, unknown> = {};

  @Output() readonly ready = new EventEmitter<ChartReadyEvent>();
  @Output() readonly error = new EventEmitter<ChartErrorEvent>();

  private wrapper: google.visualization.ChartWrapper;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    private readonly loader: GoogleChartsLoaderService) {
  }

  public get chartWrapper(): google.visualization.ChartWrapper {
    if (!this.wrapper) {
      throw new Error('The chart wrapper is not available until the chart has been created.');
    }

    return this.wrapper;
  }

  ngOnInit(): void {
    this.loader.load(getPackageForChartType(this.type))
      .then(() => {
        this.wrapper = new google.visualization.ChartWrapper({
          container: this.elementRef.nativeElement,
          chartType: this.type,
          dataTable: this.createDataTable(),
          options: this.mergeOptions()
        });

        this.registerEvents();
        this.wrapper.draw();
      })
      .catch((reason: Error) => this.zone.run(() => this.error.emit({
        id: 'loading-failed',
        message: reason.message,
        detailedMessage: reason.message,
        options: {}
      })));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.wrapper) {
      return;
    }

    if (changes['data'] || changes['columns']) {
      this.wrapper.setDataTable(this.createDataTable());
    }

    if (changes['type']) {
      this.wrapper.setChartType(this.type);
    }

    if (changes['options'] || changes['title'] || changes['width'] || changes['height']) {
      this.wrapper.setOptions(this.mergeOptions());
    }

    this.wrapper.draw();
  }

  ngOnDestroy(): void {
    if (this.wrapper) {
      google.visualization.events.removeAllListeners(this.wrapper);
    }
  }

  private createDataTable(): google.visualization.DataTable {
    const rows = this.data ?? [];
    const firstRowIsData = this.columns == null;
    const table = firstRowIsData ? rows : [this.columns, ...rows];

    return google.visualization.arrayToDataTable(table, firstRowIsData);
  }

  private mergeOptions(): Record<string, unknown> {
    return { title: this.title, width: this.width, height: this.height, ...this.options };
  }

  private registerEvents(): void {
    google.visualization.events.removeAllListeners(this.wrapper);

    google.visualization.events.addListener(this.wrapper, 'ready', () =>
      this.zone.run(() => this.ready.emit({ chart: this.wrapper.getChart() })));

    google.visualization.events.addListener(this.wrapper, 'error', (event: ChartErrorEvent) =>
      this.zone.run(() => this.error.emit(event)));
  }
}
